import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../lib/auth-context.js";
import { listBills } from "../../lib/api-modules/bills.js";
import { listIncomeEvents } from "../../lib/api-modules/income-events.js";
import { listSpendingTransactions } from "../../lib/api-modules/spending-transactions.js";
import {
  updateFireAssumptions,
  type UpdateFireAssumptionsInput,
  type WithdrawalStrategy as ApiWithdrawalStrategy,
} from "../../lib/api-modules/households.js";
import { savingsRate } from "../../lib/util/fire.js";
import {
  accountTaxMix,
  buildRetirementPlan,
  estimateRetirementTaxRatePct,
} from "../../lib/util/retirement-plan.js";
import {
  compareWithdrawalStrategies,
  historicalReturnStats,
  monteCarloSimulation,
  sequenceOfReturnsScenarios,
  trinityBackTest,
  type StrategyComparisonRow,
  type TrinityBackTestInput,
  type MonteCarloInput,
  type TrinityWindow,
} from "../../lib/util/stress-test.js";
import type { WithdrawalStrategyConfig } from "../../lib/util/withdrawal-strategies.js";
import type { SpendingSmileConfig } from "../../lib/util/spending-smile.js";
import type { BondTentConfig } from "../../lib/util/bond-tent.js";
import {
  HISTORICAL_DATA_SOURCE,
  HISTORICAL_LATEST_CAPE,
  HISTORICAL_REAL_RETURNS,
  HISTORICAL_REAL_RETURNS_WITH_CAPE,
} from "../../lib/data/historical-returns.js";
import { formatCentsCompact } from "../../lib/util/money.js";
import {
  Button,
  Card,
  CheckboxField,
  NumberField,
  Pill,
  SelectField,
  Tab,
  Tabs,
} from "../design-system/index.js";
import { useToast } from "../toast.js";
import { MonteCarloChart } from "./MonteCarloChart.js";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/**
 * Phase 6 Session 2 — the FIRE confidence layer.
 *
 * Renders three views beneath FireTile + ShockinglySimpleMathTable:
 *   - Trinity Study back-test: every rolling 30-year historical window
 *     1928→2025 replayed with the household's portfolio + spend at the
 *     current allocation.
 *   - Sequence-of-returns risk: the most-recent-30-years of real returns
 *     replayed in three orderings (forward / reversed / shuffled) so the
 *     order-effect is visible.
 *   - Monte Carlo: 1,000 stochastic 30-year futures from a normal
 *     distribution centered on the user's expected return.
 *
 * All three tabs share the same household inputs (annual spend computed
 * via savingsRate, expected return + withdrawal rate from fire_assumptions).
 * Reuses TanStack query cache that FireTile already populated on this
 * dashboard render — no new network round-trips.
 *
 * Privacy mode masks $ headlines + tooltip values; chart shapes stay
 * visible because the spread itself is the lesson.
 */

const DEFAULT_WITHDRAWAL_RATE_PCT = 4;
const DEFAULT_EXPECTED_RETURN_RATE_PCT = 5;
const SEQUENCE_YEARS = 30;
const DEFAULT_SIMULATIONS = 1000;
const DEFAULT_EQUITY_PCT = 60;
const DEFAULT_BOND_PCT = 40;

const STRATEGY_LABELS: Readonly<Record<ApiWithdrawalStrategy, string>> = {
  bengen_constant: "Fixed amount (the 4% rule)",
  guyton_klinger: "Spending guardrails",
  vpw: "Percent of savings by age",
  cape_based: "Based on market prices",
  dynamic_band: "Flexible band",
};

const STRATEGY_DESCRIPTIONS: Readonly<Record<ApiWithdrawalStrategy, string>> = {
  bengen_constant:
    "Withdraw a constant inflation-adjusted amount every year (the original Trinity Study rule).",
  guyton_klinger:
    "Cut spending after a market crash; raise after a strong run-up. Guardrails ±20%.",
  vpw: "Spend a fixed age-by-age % of current portfolio. Never runs out, but spending swings.",
  cape_based:
    "Tie withdrawal rate to Shiller CAPE: spend less when stocks are expensive, more when cheap.",
  dynamic_band:
    "Flex spend within ±band % of initial target based on portfolio drift.",
};

type TabKey = "trinity" | "sequence" | "monte_carlo" | "compare";

interface StressTestPanelProps {
  readonly hideValues: boolean;
  readonly fiAssetsCents: number;
  readonly byAccountType?: Readonly<Record<string, number>> | undefined;
}

/** Where a simulation starts: your projected retirement, or right now. */
type StartPoint = "plan" | "today";

export function StressTestPanel({
  hideValues,
  fiAssetsCents,
  byAccountType,
}: StressTestPanelProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<TabKey>("trinity");
  const [startPoint, setStartPoint] = useState<StartPoint>("plan");
  const [showSettings, setShowSettings] = useState(false);

  // === data fetch (cache-hits FireTile's queries) =============================
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const trailingFrom = useMemo(() => {
    const d = new Date(today);
    d.setUTCMonth(d.getUTCMonth() - 12);
    return d.toISOString().slice(0, 10);
  }, [today]);

  const billsQuery = useQuery({
    queryKey: ["bills", { includeArchived: false }],
    queryFn: () => listBills(false),
  });
  const incomeQuery = useQuery({
    queryKey: ["income-events", { from: trailingFrom }],
    queryFn: () => listIncomeEvents({ from: trailingFrom }),
  });
  const spendQuery = useQuery({
    queryKey: ["spending-transactions", { from: trailingFrom, to: today }],
    queryFn: () =>
      listSpendingTransactions({ from: trailingFrom, to: today }),
  });

  // === assumptions ===========================================================
  const withdrawalRatePct =
    user?.fire_assumptions?.withdrawal_rate_pct ?? DEFAULT_WITHDRAWAL_RATE_PCT;
  const expectedReturnPct =
    user?.fire_assumptions?.expected_return_rate_pct ??
    DEFAULT_EXPECTED_RETURN_RATE_PCT;
  const retirementAge =
    user?.fire_assumptions?.retirement_target_age ?? 65;

  // === the household's plan ==================================================
  const isLoading =
    billsQuery.isLoading || incomeQuery.isLoading || spendQuery.isLoading;
  const isError =
    billsQuery.isError || incomeQuery.isError || spendQuery.isError;

  const savings = useMemo(() => {
    if (isLoading || isError) return null;
    return savingsRate({
      income_events: incomeQuery.data?.income_events ?? [],
      bills: billsQuery.data?.bills ?? [],
      spending_transactions: spendQuery.data?.spending_transactions ?? [],
      today,
      lookback_months: 12,
    });
  }, [
    billsQuery.data,
    incomeQuery.data,
    spendQuery.data,
    today,
    isLoading,
    isError,
  ]);

  const annualSpendCents =
    savings === null
      ? null
      : (user?.fire_assumptions?.annual_spend_override_cents ??
        savings.annualized_spend_cents);

  const taxRatePct = useMemo(
    () =>
      annualSpendCents === null
        ? 0
        : estimateRetirementTaxRatePct({
            annual_spend_cents: annualSpendCents,
            mix: accountTaxMix(byAccountType),
          }),
    [annualSpendCents, byAccountType],
  );

  // Simulations start from what this household will actually have. They
  // used to start from spend x 25, which gave every household the same
  // success rate whatever its savings or spending.
  const plan = useMemo(() => {
    const fa = user?.fire_assumptions;
    return buildRetirementPlan({
      fi_assets_cents: fiAssetsCents,
      monthly_net_savings_cents: savings?.monthly_net_savings_cents ?? 0,
      annual_return_rate_pct: expectedReturnPct,
      current_year: Number(today.slice(0, 4)),
      retirement_target_age: retirementAge,
      birth_year_primary: fa?.birth_year_primary,
      birth_year_spouse: fa?.birth_year_spouse,
      social_security_pia_cents: fa?.social_security_pia_cents,
      social_security_claim_age: fa?.social_security_claim_age,
      social_security_pia_spouse_cents: fa?.social_security_pia_spouse_cents,
      social_security_claim_age_spouse: fa?.social_security_claim_age_spouse,
      pre_medicare_healthcare_annual_cents:
        fa?.pre_medicare_healthcare_annual_cents,
      retire_today: startPoint === "today",
    });
  }, [
    user?.fire_assumptions,
    fiAssetsCents,
    savings,
    expectedReturnPct,
    today,
    retirementAge,
    startPoint,
  ]);
  const ageAtRetirement = plan.age_at_retirement;
  const horizonYears = plan.horizon_years;
  const returnStats = useMemo(
    () => historicalReturnStats(HISTORICAL_REAL_RETURNS),
    [],
  );

  // === Phase 6.3 strategy + smile + bond-tent state ==========================
  // Local state initialized from fire_assumptions; "Save" persists to DDB.
  const [activeStrategy, setActiveStrategy] = useState<ApiWithdrawalStrategy>(
    user?.fire_assumptions?.withdrawal_strategy ?? "bengen_constant",
  );
  const [smileEnabled, setSmileEnabled] = useState<boolean>(
    user?.fire_assumptions?.spending_smile_enabled ?? false,
  );
  const [bondTentEnabled, setBondTentEnabled] = useState<boolean>(
    user?.fire_assumptions?.bond_tent_enabled ?? false,
  );
  const [bondTentFloor, setBondTentFloor] = useState<number>(
    user?.fire_assumptions?.bond_tent_floor_pct ?? 40,
  );
  const [bondTentCeiling, setBondTentCeiling] = useState<number>(
    user?.fire_assumptions?.bond_tent_ceiling_pct ?? 60,
  );
  const [bondTentRampStart, setBondTentRampStart] = useState<number>(
    user?.fire_assumptions?.bond_tent_ramp_start_years ?? 5,
  );
  const [bondTentRampEnd, setBondTentRampEnd] = useState<number>(
    user?.fire_assumptions?.bond_tent_ramp_end_years ?? 5,
  );

  // Re-sync local state when /v1/me payload changes (e.g. another save
  // landed from a different tab). Keeps the picker in sync without forcing
  // a full unmount.
  useEffect(() => {
    if (!user?.fire_assumptions) return;
    const fa = user.fire_assumptions;
    if (fa.withdrawal_strategy) setActiveStrategy(fa.withdrawal_strategy);
    if (fa.spending_smile_enabled !== undefined)
      setSmileEnabled(fa.spending_smile_enabled);
    if (fa.bond_tent_enabled !== undefined)
      setBondTentEnabled(fa.bond_tent_enabled);
    if (fa.bond_tent_floor_pct !== undefined)
      setBondTentFloor(fa.bond_tent_floor_pct);
    if (fa.bond_tent_ceiling_pct !== undefined)
      setBondTentCeiling(fa.bond_tent_ceiling_pct);
    if (fa.bond_tent_ramp_start_years !== undefined)
      setBondTentRampStart(fa.bond_tent_ramp_start_years);
    if (fa.bond_tent_ramp_end_years !== undefined)
      setBondTentRampEnd(fa.bond_tent_ramp_end_years);
  }, [user?.fire_assumptions]);

  const saveMutation = useMutation({
    mutationFn: (input: UpdateFireAssumptionsInput) => {
      if (!user) throw new Error("Not signed in");
      return updateFireAssumptions(user.primary_household_id, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.show("Stress-test settings saved.");
      setShowSettings(false);
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Failed to save";
      toast.show(msg);
    },
  });

  // Memoized strategy / smile / bond-tent configs piped into the helpers.
  const strategyConfig: WithdrawalStrategyConfig = useMemo(
    () => ({ strategy: activeStrategy }),
    [activeStrategy],
  );
  const smileConfig: SpendingSmileConfig = useMemo(
    () => ({ enabled: smileEnabled, curve: "blanchett_2014" }),
    [smileEnabled],
  );
  const bondTentConfig: BondTentConfig = useMemo(
    () => ({
      enabled: bondTentEnabled,
      retirement_age: retirementAge,
      bond_floor_pct: bondTentFloor,
      bond_ceiling_pct: bondTentCeiling,
      ramp_start_years_before: bondTentRampStart,
      ramp_end_years_after: bondTentRampEnd,
    }),
    [
      bondTentEnabled,
      retirementAge,
      bondTentFloor,
      bondTentCeiling,
      bondTentRampStart,
      bondTentRampEnd,
    ],
  );

  // === computations ===========================================================
  const retirementPortfolioCents = plan.projected_portfolio_cents;

  const trinity = useMemo(() => {
    if (annualSpendCents === null || retirementPortfolioCents === 0) return null;
    const input: TrinityBackTestInput = {
      portfolio_value_cents: retirementPortfolioCents,
      annual_spend_cents: annualSpendCents,
      equity_pct: DEFAULT_EQUITY_PCT,
      bond_pct: DEFAULT_BOND_PCT,
      horizon_years: horizonYears,
      // CAPE strategy needs the augmented dataset; other strategies
      // ignore the extra column.
      returns:
        activeStrategy === "cape_based"
          ? HISTORICAL_REAL_RETURNS_WITH_CAPE
          : HISTORICAL_REAL_RETURNS,
      withdrawal_strategy_config: strategyConfig,
      target_withdrawal_rate_pct: withdrawalRatePct,
      age_at_retirement: ageAtRetirement,
      spending_smile: smileConfig,
      bond_tent: bondTentConfig,
      annual_adjustments_cents: plan.annual_adjustments_cents,
      effective_tax_rate_pct: taxRatePct,
    };
    return trinityBackTest(input);
  }, [
    annualSpendCents,
    retirementPortfolioCents,
    horizonYears,
    plan.annual_adjustments_cents,
    taxRatePct,
    activeStrategy,
    strategyConfig,
    withdrawalRatePct,
    ageAtRetirement,
    smileConfig,
    bondTentConfig,
  ]);

  const sequence = useMemo(() => {
    if (annualSpendCents === null || retirementPortfolioCents === 0) return null;
    // Most-recent 30 years of real S&P returns — concrete grounding for
    // the lesson rather than abstract numbers.
    const recent30 = HISTORICAL_REAL_RETURNS.slice(-SEQUENCE_YEARS).map(
      (y) => y.equity_real_pct,
    );
    return sequenceOfReturnsScenarios({
      portfolio_value_cents: retirementPortfolioCents,
      annual_spend_cents: annualSpendCents,
      returns_series: recent30,
      seed: 42,
    });
  }, [annualSpendCents, retirementPortfolioCents]);

  const monteCarlo = useMemo(() => {
    if (annualSpendCents === null || retirementPortfolioCents === 0) return null;
    const input: MonteCarloInput = {
      portfolio_value_cents: retirementPortfolioCents,
      annual_spend_cents: annualSpendCents,
      expected_return_pct: returnStats.equity_mean_pct,
      return_stddev_pct: returnStats.equity_stddev_pct,
      bond_mean_pct: returnStats.bond_mean_pct,
      bond_stddev_pct: returnStats.bond_stddev_pct,
      horizon_years: horizonYears,
      simulations: DEFAULT_SIMULATIONS,
      seed: 42,
      withdrawal_strategy_config: strategyConfig,
      target_withdrawal_rate_pct: withdrawalRatePct,
      age_at_retirement: ageAtRetirement,
      spending_smile: smileConfig,
      bond_tent: bondTentConfig,
      equity_pct: DEFAULT_EQUITY_PCT,
      bond_pct: DEFAULT_BOND_PCT,
      cape_anchor: HISTORICAL_LATEST_CAPE,
      annual_adjustments_cents: plan.annual_adjustments_cents,
      effective_tax_rate_pct: taxRatePct,
    };
    return monteCarloSimulation(input);
  }, [
    annualSpendCents,
    retirementPortfolioCents,
    returnStats,
    horizonYears,
    plan.annual_adjustments_cents,
    taxRatePct,
    strategyConfig,
    withdrawalRatePct,
    ageAtRetirement,
    smileConfig,
    bondTentConfig,
  ]);

  // Compare tab — runs Trinity + Monte Carlo for all 5 strategies. Lower
  // simulation count (300) keeps it interactive on the first dashboard
  // paint; the headline tab still uses the full 1000.
  const comparisonRows = useMemo<readonly StrategyComparisonRow[] | null>(
    () => {
      if (annualSpendCents === null || retirementPortfolioCents === 0) {
        return null;
      }
      return compareWithdrawalStrategies({
        portfolio_value_cents: retirementPortfolioCents,
        annual_spend_cents: annualSpendCents,
        equity_pct: DEFAULT_EQUITY_PCT,
        bond_pct: DEFAULT_BOND_PCT,
        horizon_years: horizonYears,
        returns: HISTORICAL_REAL_RETURNS_WITH_CAPE,
        expected_return_pct: returnStats.equity_mean_pct,
        return_stddev_pct: returnStats.equity_stddev_pct,
        bond_mean_pct: returnStats.bond_mean_pct,
        bond_stddev_pct: returnStats.bond_stddev_pct,
        annual_adjustments_cents: plan.annual_adjustments_cents,
        effective_tax_rate_pct: taxRatePct,
        simulations: 300,
        seed: 42,
        target_withdrawal_rate_pct: withdrawalRatePct,
        age_at_retirement: ageAtRetirement,
        spending_smile: smileConfig,
        bond_tent: bondTentConfig,
        cape_anchor: HISTORICAL_LATEST_CAPE,
      });
    },
    [
      annualSpendCents,
      retirementPortfolioCents,
      returnStats,
      horizonYears,
      plan.annual_adjustments_cents,
      taxRatePct,
      withdrawalRatePct,
      ageAtRetirement,
      smileConfig,
      bondTentConfig,
    ],
  );

  // === render ================================================================
  return (
    <Card className="mb-6" elevated>
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
            Stress test
          </p>
          <p className="mt-0.5 text-xs text-[var(--color-muted)]">
            How a mix of {DEFAULT_EQUITY_PCT}% stocks and {DEFAULT_BOND_PCT}%
            bonds holds up through past markets and 1,000 random futures.
          </p>
        </div>
        <p
          className="text-xs text-[var(--color-faint)]"
          data-testid="current-net-worth-marker"
          title="Net worth minus 529, UTMA and donor-advised accounts"
        >
          Assets for retirement:{" "}
          <span className="font-mono tabular-nums">
            {formatCentsCompact(fiAssetsCents, { hidden: hideValues })}
          </span>
        </p>
      </div>

      <div
        className="mb-4 rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-elevated)] p-3 text-sm"
        data-testid="stress-plan-summary"
      >
        <div className="mb-2 flex flex-wrap gap-2" role="group" aria-label="Simulation start">
          <Button
            variant={startPoint === "plan" ? "primary" : "ghost"}
            size="xs"
            onClick={() => setStartPoint("plan")}
            aria-pressed={startPoint === "plan"}
          >
            Our plan
          </Button>
          <Button
            variant={startPoint === "today" ? "primary" : "ghost"}
            size="xs"
            onClick={() => setStartPoint("today")}
            aria-pressed={startPoint === "today"}
          >
            Retire today
          </Button>
        </div>
        {annualSpendCents !== null && (
          <p className="text-[var(--color-muted)]">
            Starting at age {plan.age_at_retirement} in {plan.retirement_year}{" "}
            with{" "}
            <span className="font-mono tabular-nums text-[var(--color-text)]">
              {formatCentsCompact(retirementPortfolioCents, {
                hidden: hideValues,
              })}
            </span>
            , spending{" "}
            <span className="font-mono tabular-nums text-[var(--color-text)]">
              {formatCentsCompact(annualSpendCents, { hidden: hideValues })}
            </span>{" "}
            a year plus about {taxRatePct.toFixed(0)}% tax, for{" "}
            {plan.horizon_years} years.
          </p>
        )}
        {!plan.has_ages && (
          <p className="mt-1 text-[var(--color-muted)]">
            Add your birth year in FIRE settings to project your savings to
            retirement. Until then this starts from what you have today.
          </p>
        )}
      </div>

      {isLoading ? (
        <p className="py-8 text-center text-sm text-[var(--color-muted)]">Loading…</p>
      ) : isError ? (
        <p
          className="py-8 text-center text-sm text-[var(--color-danger)]"
          data-testid="stress-test-error"
        >
          Couldn't load stress-test inputs.
        </p>
      ) : annualSpendCents === null || annualSpendCents === 0 ? (
        <div
          className="rounded-md border border-dashed border-[var(--color-border-default)] p-8 text-center"
          data-testid="stress-test-empty"
        >
          <p className="font-medium text-[var(--color-text)]">
            Add bills + spending to run the stress test.
          </p>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Trinity / sequence / Monte Carlo all need an annual-spend baseline.
          </p>
        </div>
      ) : (
        <>
          {/* Phase 6.3 — strategy picker + smile/bond-tent settings disclosure.
              Lives ABOVE the tabs so changes re-parametrize Trinity + Monte
              Carlo + Compare in lockstep. Sequence stays strategy-agnostic
              (it's the order-of-returns lesson, not the withdrawal rule). */}
          <div className="mb-4 rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-card)] p-3">
            <div className="flex flex-wrap items-end gap-3">
              <SelectField
                label="Withdrawal strategy"
                helper={STRATEGY_DESCRIPTIONS[activeStrategy]}
                containerClassName="min-w-[260px]"
                value={activeStrategy}
                onChange={(e) =>
                  setActiveStrategy(e.target.value as ApiWithdrawalStrategy)
                }
                data-testid="strategy-picker"
              >
                {(Object.keys(STRATEGY_LABELS) as ApiWithdrawalStrategy[]).map(
                  (key) => (
                    <option key={key} value={key}>
                      {STRATEGY_LABELS[key]}
                    </option>
                  ),
                )}
              </SelectField>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSettings((s) => !s)}
                aria-expanded={showSettings}
                aria-controls="stress-test-settings"
                data-testid="settings-toggle"
              >
                {showSettings ? "Hide" : "More"} settings
              </Button>
            </div>
            {showSettings && (
              <div
                id="stress-test-settings"
                className="mt-4 grid gap-4 border-t border-[var(--color-border-subtle)] pt-4 sm:grid-cols-2"
              >
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
                    Spending smile (Blanchett)
                  </p>
                  <CheckboxField
                    className="mt-2"
                    label="Apply spending smile"
                    helper="Real spend dips through ages 65→80, rises in late retirement (healthcare)."
                    checked={smileEnabled}
                    onChange={(e) => setSmileEnabled(e.target.checked)}
                    data-testid="smile-toggle"
                  />
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
                    Bond tent (Pfau / Kitces glidepath)
                  </p>
                  <CheckboxField
                    className="mt-2"
                    label="Apply bond tent"
                    helper="Glide bond allocation up around retirement to defuse sequence risk."
                    checked={bondTentEnabled}
                    onChange={(e) => setBondTentEnabled(e.target.checked)}
                    data-testid="bond-tent-toggle"
                  />
                  {bondTentEnabled && (
                    <div className="mt-3 grid gap-2 grid-cols-2">
                      <NumberField
                        label="Bond floor %"
                        value={bondTentFloor}
                        min={0}
                        max={100}
                        step={5}
                        onChange={(e) =>
                          setBondTentFloor(Number(e.target.value))
                        }
                        data-testid="bond-tent-floor"
                      />
                      <NumberField
                        label="Bond ceiling %"
                        value={bondTentCeiling}
                        min={0}
                        max={100}
                        step={5}
                        onChange={(e) =>
                          setBondTentCeiling(Number(e.target.value))
                        }
                        data-testid="bond-tent-ceiling"
                      />
                      <NumberField
                        label="Years before retirement"
                        value={bondTentRampStart}
                        min={0}
                        max={40}
                        step={1}
                        onChange={(e) =>
                          setBondTentRampStart(Number(e.target.value))
                        }
                      />
                      <NumberField
                        label="Years after retirement"
                        value={bondTentRampEnd}
                        min={0}
                        max={40}
                        step={1}
                        onChange={(e) =>
                          setBondTentRampEnd(Number(e.target.value))
                        }
                      />
                    </div>
                  )}
                </div>
                <div className="sm:col-span-2 flex items-center justify-end gap-2">
                  <p className="text-[10px] text-[var(--color-faint)]">
                    Saving persists these settings to your household so the
                    strategy + overlays survive a refresh.
                  </p>
                  <Button
                    size="sm"
                    onClick={() =>
                      saveMutation.mutate({
                        withdrawal_strategy: activeStrategy,
                        spending_smile_enabled: smileEnabled,
                        spending_smile_curve: smileEnabled
                          ? "blanchett_2014"
                          : null,
                        bond_tent_enabled: bondTentEnabled,
                        bond_tent_floor_pct: bondTentEnabled
                          ? bondTentFloor
                          : null,
                        bond_tent_ceiling_pct: bondTentEnabled
                          ? bondTentCeiling
                          : null,
                        bond_tent_ramp_start_years: bondTentEnabled
                          ? bondTentRampStart
                          : null,
                        bond_tent_ramp_end_years: bondTentEnabled
                          ? bondTentRampEnd
                          : null,
                      })
                    }
                    disabled={saveMutation.isPending}
                    data-testid="settings-save"
                  >
                    {saveMutation.isPending ? "Saving…" : "Save"}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <Tabs value={tab} onChange={(t) => setTab(t as TabKey)} ariaLabel="Stress test views">
            <Tab value="trinity" label="Past markets">
              <TrinityTab
                horizonYears={horizonYears}
                result={trinity}
                annualSpendCents={annualSpendCents}
                retirementPortfolioCents={retirementPortfolioCents}
                hideValues={hideValues}
              />
            </Tab>
            <Tab value="sequence" label="Bad timing">
              <SequenceTab
                result={sequence}
                retirementPortfolioCents={retirementPortfolioCents}
                annualSpendCents={annualSpendCents}
                hideValues={hideValues}
              />
            </Tab>
            <Tab value="monte_carlo" label="Random futures">
              <MonteCarloTab
                result={monteCarlo}
                annualSpendCents={annualSpendCents}
                retirementPortfolioCents={retirementPortfolioCents}
                expectedReturnPct={returnStats.equity_mean_pct}
                horizonYears={horizonYears}
                hideValues={hideValues}
              />
            </Tab>
            <Tab value="compare" label="Compare strategies">
              <StrategyCompareTab
                rows={comparisonRows}
                hideValues={hideValues}
                activeStrategy={activeStrategy}
              />
            </Tab>
          </Tabs>
        </>
      )}

      <p className="mt-4 text-[10px] text-[var(--color-faint)]">
        Source: {HISTORICAL_DATA_SOURCE}. Withdrawals constant in real terms
        (Bengen). Simulation seed 42 (deterministic).
      </p>
    </Card>
  );
}

// ============================================================================
// Trinity Study tab
// ============================================================================

interface TrinityTabProps {
  readonly horizonYears: number;
  readonly result: ReturnType<typeof trinityBackTest> | null;
  readonly annualSpendCents: number;
  readonly retirementPortfolioCents: number;
  readonly hideValues: boolean;
}

function TrinityTab({
  horizonYears,
  result,
  annualSpendCents,
  retirementPortfolioCents,
  hideValues,
}: TrinityTabProps) {
  if (!result) return <p className="text-sm text-[var(--color-muted)]">No data.</p>;

  const successPctRounded = Math.round(result.success_pct);
  const tone: "accent" | "success" | "warning" | "danger" =
    successPctRounded >= 95
      ? "success"
      : successPctRounded >= 85
        ? "accent"
        : successPctRounded >= 70
          ? "warning"
          : "danger";

  return (
    <div data-testid="trinity-tab">
      {/* Headline */}
      <div className="mb-4 flex flex-wrap items-baseline gap-3">
        <p
          className="font-display text-4xl font-medium tabular-nums text-[var(--color-text)]"
          data-testid="trinity-success-rate"
        >
          {successPctRounded}%
        </p>
        <Pill tone={tone}>survived</Pill>
        <p className="text-sm text-[var(--color-muted)]">
          of {result.windows.length} historical {horizonYears}-year windows,{" "}
          {DEFAULT_EQUITY_PCT}/{DEFAULT_BOND_PCT} allocation,{" "}
          <span className="font-mono tabular-nums">
            {formatCentsCompact(retirementPortfolioCents, { hidden: hideValues })}
          </span>{" "}
          starting portfolio,{" "}
          <span className="font-mono tabular-nums">
            {formatCentsCompact(annualSpendCents, { hidden: hideValues })}
          </span>{" "}
          annual spend.
        </p>
      </div>

      {/* Heat-strip — one cell per starting year. Survivors green, failures red. */}
      <div className="mb-4">
        <div
          className="flex flex-wrap gap-px"
          role="img"
          aria-label={`Heat strip of historical ${horizonYears}-year window outcomes`}
        >
          {result.windows.map((w) => (
            <HeatCell key={w.start_year} window={w} hideValues={hideValues} />
          ))}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] text-[var(--color-muted)]">
          <span>{result.windows[0]!.start_year}</span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-sm bg-[var(--color-success-soft)]" />
            survived
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-sm bg-[var(--color-danger-soft)]" />
            ran out
          </span>
          <span className="ml-auto">{result.windows[result.windows.length - 1]!.start_year}</span>
        </div>
      </div>

      {/* Worst / best window callouts */}
      <div className="grid gap-3 sm:grid-cols-2">
        {result.worst_window && (
          <WindowCallout
            label={result.worst_window.ran_out ? "Worst (ran out)" : "Worst surviving"}
            window={result.worst_window}
            tone={result.worst_window.ran_out ? "danger" : "neutral"}
            hideValues={hideValues}
          />
        )}
        {result.best_window && (
          <WindowCallout
            label="Best surviving"
            window={result.best_window}
            tone="success"
            hideValues={hideValues}
          />
        )}
      </div>
    </div>
  );
}

function HeatCell({
  window,
  hideValues,
}: {
  readonly window: TrinityWindow;
  readonly hideValues: boolean;
}) {
  const bg = window.ran_out
    ? "bg-[var(--color-danger-soft)] hover:bg-[var(--color-danger)]"
    : "bg-[var(--color-success-soft)] hover:bg-[var(--color-success)]";
  const title = window.ran_out
    ? `${window.start_year}–${window.end_year}: ran out in ${window.ran_out_year}`
    : `${window.start_year}–${window.end_year}: ${
        hideValues
          ? "survived"
          : `survived, ended at ${formatCentsCompact(window.ending_balance_cents)}`
      }`;
  return (
    <span
      className={`h-5 w-3 rounded-sm transition-colors ${bg}`}
      title={title}
      data-testid={`heat-cell-${window.start_year}`}
      data-ran-out={window.ran_out ? "true" : "false"}
    />
  );
}

function WindowCallout({
  label,
  window,
  tone,
  hideValues,
}: {
  readonly label: string;
  readonly window: TrinityWindow;
  readonly tone: "danger" | "success" | "neutral";
  readonly hideValues: boolean;
}) {
  const accent =
    tone === "danger"
      ? "text-[var(--color-danger)]"
      : tone === "success"
        ? "text-[var(--color-success)]"
        : "text-[var(--color-text)]";
  return (
    <div className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-card)] p-3">
      <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
        {label}
      </p>
      <p className="mt-1 text-sm text-[var(--color-text)]">
        <span className="font-mono tabular-nums">{window.start_year}–{window.end_year}</span>
      </p>
      <p className={`mt-1 font-mono text-base font-medium tabular-nums ${accent}`}>
        {window.ran_out
          ? `Ran out in ${window.ran_out_year}`
          : formatCentsCompact(window.ending_balance_cents, { hidden: hideValues })}
      </p>
    </div>
  );
}

// ============================================================================
// Sequence-of-returns tab
// ============================================================================

interface SequenceTabProps {
  readonly result: ReturnType<typeof sequenceOfReturnsScenarios> | null;
  readonly retirementPortfolioCents: number;
  readonly annualSpendCents: number;
  readonly hideValues: boolean;
}

function SequenceTab({
  result,
  retirementPortfolioCents,
  annualSpendCents,
  hideValues,
}: SequenceTabProps) {
  // Combine all three paths into a single dataset for Recharts:
  // [{year_offset, forward, reversed, shuffled}, ...]
  const data = useMemo(() => {
    if (!result) return null;
    const len = result.forward_path.length;
    return Array.from({ length: len }, (_, i) => ({
      year_offset: result.forward_path[i]!.year_offset,
      forward: result.forward_path[i]!.balance_cents,
      reversed: result.reversed_path[i]!.balance_cents,
      shuffled: result.shuffled_path[i]!.balance_cents,
    }));
  }, [result]);

  if (!result || !data) {
    return <p className="text-sm text-[var(--color-muted)]">No data.</p>;
  }

  const fwdEnd = result.forward_path[result.forward_path.length - 1]!.balance_cents;
  const revEnd = result.reversed_path[result.reversed_path.length - 1]!.balance_cents;
  const shfEnd = result.shuffled_path[result.shuffled_path.length - 1]!.balance_cents;

  return (
    <div data-testid="sequence-tab">
      <div className="mb-4">
        <p className="text-sm text-[var(--color-text)]">
          Same average return —{" "}
          <span className="font-mono tabular-nums">
            {result.geometric_mean_pct.toFixed(2)}%
          </span>{" "}
          geometric mean — across all three orderings. Sequence under
          withdrawals decides the outcome.
        </p>
      </div>

      <div className="h-[260px] w-full" data-testid="sequence-chart">
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <LineChart
            data={data}
            margin={{ top: 16, right: 16, bottom: 8, left: 0 }}
          >
            <CartesianGrid strokeOpacity={0.1} vertical={false} />
            <XAxis
              dataKey="year_offset"
              tick={{ fontSize: 11, fill: "currentColor", fillOpacity: 0.7 }}
              tickFormatter={(y: number) => `Y${y}`}
              minTickGap={20}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "currentColor", fillOpacity: 0.7 }}
              tickFormatter={(v: number) =>
                hideValues ? "••••" : formatCentsCompact(v)
              }
              width={64}
            />
            <Tooltip
              content={
                hideValues
                  ? () => null
                  : ({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0)
                        return null;
                      return (
                        <div className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-3 py-2 text-xs shadow-[var(--shadow-card)]">
                          <div className="font-medium text-[var(--color-text)]">
                            Year {label}
                          </div>
                          <div className="mt-1 grid grid-cols-[auto_auto] gap-x-3 font-mono tabular-nums">
                            {payload.map((p) => (
                              <span key={p.name} className="contents">
                                <span style={{ color: p.color }}>{p.name}</span>
                                <span className="text-[var(--color-text)]">
                                  {formatCentsCompact(Number(p.value))}
                                </span>
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    }
              }
            />
            <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
            <Line
              type="monotone"
              dataKey="forward"
              name="Forward"
              stroke="var(--color-accent)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="reversed"
              name="Reversed"
              stroke="var(--color-success)"
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="shuffled"
              name="Shuffled"
              stroke="var(--color-warning)"
              strokeWidth={2}
              strokeDasharray="2 3"
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <SequenceCallout
          label="Forward"
          start={retirementPortfolioCents}
          end={fwdEnd}
          hideValues={hideValues}
        />
        <SequenceCallout
          label="Reversed"
          start={retirementPortfolioCents}
          end={revEnd}
          hideValues={hideValues}
        />
        <SequenceCallout
          label="Shuffled"
          start={retirementPortfolioCents}
          end={shfEnd}
          hideValues={hideValues}
        />
      </div>

      <p className="mt-3 text-xs text-[var(--color-muted)]">
        Series: most recent 30 years of real S&P returns ({HISTORICAL_REAL_RETURNS[HISTORICAL_REAL_RETURNS.length - SEQUENCE_YEARS]!.year}-{HISTORICAL_REAL_RETURNS[HISTORICAL_REAL_RETURNS.length - 1]!.year}). Withdrawing{" "}
        <span className="font-mono tabular-nums">
          {formatCentsCompact(annualSpendCents, { hidden: hideValues })}
        </span>{" "}
        per year (constant in real terms).
      </p>
    </div>
  );
}

function SequenceCallout({
  label,
  start,
  end,
  hideValues,
}: {
  readonly label: string;
  readonly start: number;
  readonly end: number;
  readonly hideValues: boolean;
}) {
  const delta = end - start;
  const tone = delta < 0 ? "text-[var(--color-danger)]" : "text-[var(--color-success)]";
  return (
    <div className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-card)] p-3">
      <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
        {label} — final balance
      </p>
      <p className="mt-1 font-mono text-base font-medium tabular-nums text-[var(--color-text)]">
        {formatCentsCompact(end, { hidden: hideValues })}
      </p>
      <p className={`mt-0.5 text-xs font-mono tabular-nums ${tone}`}>
        {hideValues ? "••••" : `${delta >= 0 ? "+" : ""}${formatCentsCompact(delta)}`}
      </p>
    </div>
  );
}

// ============================================================================
// Monte Carlo tab
// ============================================================================

interface MonteCarloTabProps {
  readonly result: ReturnType<typeof monteCarloSimulation> | null;
  readonly annualSpendCents: number;
  readonly retirementPortfolioCents: number;
  readonly expectedReturnPct: number;
  readonly horizonYears: number;
  readonly hideValues: boolean;
}

function MonteCarloTab({
  result,
  annualSpendCents,
  retirementPortfolioCents,
  expectedReturnPct,
  horizonYears,
  hideValues,
}: MonteCarloTabProps) {
  if (!result)
    return <p className="text-sm text-[var(--color-muted)]">No data.</p>;

  const successRounded = Math.round(result.success_pct);
  const tone: "accent" | "success" | "warning" | "danger" =
    successRounded >= 90
      ? "success"
      : successRounded >= 75
        ? "accent"
        : successRounded >= 50
          ? "warning"
          : "danger";

  return (
    <div data-testid="monte-carlo-tab">
      <div className="mb-4 flex flex-wrap items-baseline gap-3">
        <p
          className="font-display text-4xl font-medium tabular-nums text-[var(--color-text)]"
          data-testid="monte-carlo-success-rate"
        >
          {successRounded}%
        </p>
        <Pill tone={tone}>simulations succeeded</Pill>
        <p className="text-sm text-[var(--color-muted)]">
          {result.resolved.simulations} random {horizonYears}-year futures
          drawn from 1928–2025 returns (stocks averaged{" "}
          {expectedReturnPct.toFixed(1)}% after inflation, swinging about{" "}
          {result.resolved.return_stddev_pct.toFixed(0)}% a year), starting at{" "}
          <span className="font-mono tabular-nums">
            {formatCentsCompact(retirementPortfolioCents, { hidden: hideValues })}
          </span>
          , spending{" "}
          <span className="font-mono tabular-nums">
            {formatCentsCompact(annualSpendCents, { hidden: hideValues })}
          </span>
          /yr.
        </p>
      </div>

      <MonteCarloChart band={result.band} hideValues={hideValues} />

      <div className="mt-4 grid gap-3 sm:grid-cols-5">
        <PercentileCallout
          label="p10 (rough)"
          value={result.final_percentiles.p10}
          tone="danger"
          hideValues={hideValues}
        />
        <PercentileCallout
          label="p25"
          value={result.final_percentiles.p25}
          tone="muted"
          hideValues={hideValues}
        />
        <PercentileCallout
          label="p50 (median)"
          value={result.final_percentiles.p50}
          tone="accent"
          hideValues={hideValues}
        />
        <PercentileCallout
          label="p75"
          value={result.final_percentiles.p75}
          tone="muted"
          hideValues={hideValues}
        />
        <PercentileCallout
          label="p90 (rosy)"
          value={result.final_percentiles.p90}
          tone="success"
          hideValues={hideValues}
        />
      </div>
    </div>
  );
}

function PercentileCallout({
  label,
  value,
  tone,
  hideValues,
}: {
  readonly label: string;
  readonly value: number;
  readonly tone: "danger" | "muted" | "accent" | "success";
  readonly hideValues: boolean;
}) {
  const color =
    tone === "danger"
      ? "text-[var(--color-danger)]"
      : tone === "success"
        ? "text-[var(--color-success)]"
        : tone === "accent"
          ? "text-[var(--color-accent)]"
          : "text-[var(--color-text)]";
  return (
    <div className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-card)] p-3">
      <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
        {label}
      </p>
      <p className={`mt-1 font-mono text-sm font-medium tabular-nums ${color}`}>
        {formatCentsCompact(value, { hidden: hideValues })}
      </p>
    </div>
  );
}

// ============================================================================
// Strategy compare tab — Phase 6 Session 3
// ============================================================================

interface StrategyCompareTabProps {
  readonly rows: readonly StrategyComparisonRow[] | null;
  readonly hideValues: boolean;
  readonly activeStrategy: ApiWithdrawalStrategy;
}

function StrategyCompareTab({
  rows,
  hideValues,
  activeStrategy,
}: StrategyCompareTabProps) {
  if (!rows) return <p className="text-sm text-[var(--color-muted)]">No data.</p>;

  // Convert cents → dollars for the chart axis (Recharts numbers).
  const chartData = rows.map((r) => ({
    strategy: STRATEGY_LABELS[r.strategy],
    raw_strategy: r.strategy,
    median_dollars: r.mc_p50_end_cents / 100,
    p10_dollars: r.mc_p10_end_cents / 100,
    p90_dollars: r.mc_p90_end_cents / 100,
    trinity_pct: r.trinity_success_pct,
    mc_pct: r.mc_success_pct,
  }));

  // Headline delta vs Bengen baseline.
  const baseline = rows.find((r) => r.strategy === "bengen_constant");
  const baselineMedian = baseline?.mc_p50_end_cents ?? 0;

  return (
    <div data-testid="compare-tab">
      <div className="mb-4">
        <p className="text-sm text-[var(--color-text)]">
          Median Monte Carlo end-balance per strategy. Bars wider than the
          fixed-amount row mean a stronger expected outcome; you'll trade that for
          year-to-year spend volatility (especially with VPW).
        </p>
      </div>

      <div className="h-[260px] w-full" data-testid="compare-chart">
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 8, right: 24, bottom: 8, left: 0 }}
          >
            <CartesianGrid strokeOpacity={0.1} horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: "currentColor", fillOpacity: 0.7 }}
              tickFormatter={(v: number) =>
                hideValues ? "••••" : formatCentsCompact(v * 100)
              }
            />
            <YAxis
              type="category"
              dataKey="strategy"
              width={150}
              tick={{ fontSize: 11, fill: "currentColor", fillOpacity: 0.85 }}
            />
            <Tooltip
              content={
                hideValues
                  ? () => null
                  : ({ active, payload }) => {
                      if (!active || !payload || payload.length === 0) {
                        return null;
                      }
                      const row = payload[0]?.payload as
                        | (typeof chartData)[number]
                        | undefined;
                      if (!row) return null;
                      return (
                        <div className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-3 py-2 text-xs shadow-[var(--shadow-card)]">
                          <div className="font-medium text-[var(--color-text)]">
                            {row.strategy}
                          </div>
                          <div className="mt-1 grid grid-cols-[auto_auto] gap-x-3 font-mono tabular-nums">
                            <span className="text-[var(--color-muted)]">Median end</span>
                            <span className="text-[var(--color-text)]">
                              {formatCentsCompact(row.median_dollars * 100)}
                            </span>
                            <span className="text-[var(--color-muted)]">p10 / p90</span>
                            <span className="text-[var(--color-text)]">
                              {formatCentsCompact(row.p10_dollars * 100)} /{" "}
                              {formatCentsCompact(row.p90_dollars * 100)}
                            </span>
                            <span className="text-[var(--color-muted)]">Trinity</span>
                            <span className="text-[var(--color-text)]">
                              {Math.round(row.trinity_pct)}%
                            </span>
                            <span className="text-[var(--color-muted)]">Monte Carlo</span>
                            <span className="text-[var(--color-text)]">
                              {Math.round(row.mc_pct)}%
                            </span>
                          </div>
                        </div>
                      );
                    }
              }
            />
            <Bar dataKey="median_dollars" name="Median end balance">
              {chartData.map((d) => (
                <Cell
                  key={d.raw_strategy}
                  fill={
                    d.raw_strategy === activeStrategy
                      ? "var(--color-accent)"
                      : "var(--color-accent-soft)"
                  }
                />
              ))}
            </Bar>
            <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {rows.map((row) => {
          const delta = row.mc_p50_end_cents - baselineMedian;
          const tone =
            delta > 0
              ? "text-[var(--color-success)]"
              : delta < 0
                ? "text-[var(--color-danger)]"
                : "text-[var(--color-text)]";
          const isActive = row.strategy === activeStrategy;
          return (
            <div
              key={row.strategy}
              className={`rounded-md border p-3 ${
                isActive
                  ? "border-[var(--color-accent)] bg-[var(--color-accent-soft)]"
                  : "border-[var(--color-border-subtle)] bg-[var(--color-card)]"
              }`}
              data-testid={`compare-row-${row.strategy}`}
            >
              <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
                {STRATEGY_LABELS[row.strategy]}
              </p>
              <p className="mt-1 font-mono text-sm font-medium tabular-nums text-[var(--color-text)]">
                {formatCentsCompact(row.mc_p50_end_cents, { hidden: hideValues })}
              </p>
              {row.strategy !== "bengen_constant" && (
                <p className={`mt-1 text-[11px] font-mono tabular-nums ${tone}`}>
                  {hideValues
                    ? "••••"
                    : `${delta >= 0 ? "+" : ""}${formatCentsCompact(delta)} vs fixed amount`}
                </p>
              )}
              <p className="mt-1 text-[10px] text-[var(--color-muted)]">
                Trinity {Math.round(row.trinity_success_pct)}% / MC{" "}
                {Math.round(row.mc_success_pct)}%
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
